import React, { useState, useEffect } from 'react';
import PageHeaderTile from '../ui/PageHeaderTile';
import { Users } from 'lucide-react';
import ClientForm from '../clients/ClientForm';
import ClientsList from '../clients/ClientsList';
import axiosInstance from '../../axiosConfig';
import Tile from '../ui/Tile';

function ClientsPage(props) {
    const [clients, setClients] = useState([]);
    const [page, setPage] = useState(0);
    const [size] = useState(20);
    const [hasMore, setHasMore] = useState(true);
    const [sorting, setSorting] = useState({ key: null, direction: null });
    const [isEditing, setIsEditing] = useState(false);
    const [editingClientId, setEditingClientId] = useState(null);
    const [clientFormData, setClientFormData] = useState({
        name: '',
        vatNumber: '',
        idNumber: '',
        city: '',
        address: '',
        mol: '',
        phoneNumber: '',
    });

    const fetchClients = (page, size, sorting, reset = false) => {
        const sortParam = sorting.key ? `&sortProperty=${sorting.key}&sortDirection=${sorting.direction || 'asc'}` : '';
        axiosInstance.get(`/api/clients?page=${page}&size=${size}${sortParam}`)
            .then(response => response.data)
            .then(data => {
                if (data.length < size) {
                    setHasMore(false);
                }
                if (reset) {
                    setClients(data);
                } else {
                    setClients(prevClients => [...prevClients, ...data]);
                }
            })
            .catch(error => console.error('Error fetching clients:', error));
    };

    useEffect(() => {
        setClients([]);
        setPage(0);
        setHasMore(true);
        fetchClients(0, size, sorting, true);
    }, [sorting]);

    const loadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchClients(nextPage, size, sorting);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setClientFormData({
            ...clientFormData,
            [name]: value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await axiosInstance.put(`/api/clients/${editingClientId}`, clientFormData);
                setIsEditing(false);
                setEditingClientId(null);
            } else {
                await axiosInstance.post('/api/clients', clientFormData);
            }
            // Clear form
            setClientFormData({
                name: '',
                vatNumber: '',
                idNumber: '',
                city: '',
                address: '',
                mol: '',
                phoneNumber: '',
            });
            // Reload clients
            setClients([]);
            setPage(0);
            setHasMore(true);
            fetchClients(0, size, sorting, true);
        } catch (error) {
            console.error('Error saving client:', error);
            const errorMessage = error.response && error.response.data && error.response.data.error && error.response.data.error.message
                ? error.response.data.error.message
                : 'Failed to save client.';
            alert("Възникна грешка при запазване на клиента: " + errorMessage);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingClientId(null);
        setClientFormData({
            name: '',
            vatNumber: '',
            idNumber: '',
            city: '',
            address: '',
            mol: '',
            phoneNumber: '',
        });
    };

    const handleDelete = async (clientId) => {
        try {
            await axiosInstance.delete(`/api/clients/${clientId}`);
            setClients(prevClients => prevClients.filter(c => c.id !== clientId));
        } catch (error) {
            console.error('Error deleting client:', error);
            alert('Failed to delete client.');
        }
    };

    const handleUpdateClient = async (updatedClient) => {
        try {
            await axiosInstance.put(`/api/clients/${updatedClient.id}`, updatedClient);
            // Optimistic update
            setClients(prevClients => prevClients.map(c => c.id === updatedClient.id ? updatedClient : c));
        } catch (error) {
            console.error('Error updating client:', error);
            alert('Failed to update client.');
        }
    };

    const handleLoadClientToForm = (client) => {
        setClientFormData({
            name: client.name || '',
            vatNumber: client.vatNumber || '',
            idNumber: client.idNumber || '',
            city: client.city || '',
            address: client.address || '',
            mol: client.mol || '',
            phoneNumber: client.phoneNumber || '',
        });
        setIsEditing(false); // we just load it for creation unless edit is explicitly clicked
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="w-full max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 flex flex-col gap-8">
            <PageHeaderTile title="Клиенти" icon={Users} />
            
            <ClientForm
                formData={clientFormData}
                handleInputChange={handleInputChange}
                handleClientSubmit={handleSubmit}
                isEditing={isEditing}
                handleCancelEdit={handleCancelEdit}
            />

            <ClientsList
                clients={clients}
                handleUpdateClient={handleUpdateClient}
                handleDelete={handleDelete}
                loadMore={hasMore ? loadMore : null}
                sorting={sorting}
                setSorting={setSorting}
                loadClientToForm={handleLoadClientToForm}
            />
        </div>
    );
}

export default ClientsPage;
